import React, { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { Spinner } from "reactstrap";

import * as api from "services/api";

import StyledModal from "components/StyledModal";

import { ConfirmContractGenerationStep } from "./ConfirmContractGenerationStep";
import { ContractFormStep } from "./ContractFormStep";
import { SelectContractTypeStep } from "./SelectContractTypeStep";

import { ButtonsContainer, ModalHeader, Separator } from "./styles";

export function GenerateContractModal({
	isModalOpen,
	setIsModalOpen,
	solicitationId,
	contractSolicitation,
	lastDocumentName,
	lastDocumentURL,
	onContractGenerated
}) {
	const [step, setStep] = useState("selectContractType");
	const [contractData, setContractData] = useState(null);
	const [isGeneratingContract, setIsGeneratingContract] = useState(false);
	const [isFormValid, setIsFormValid] = useState(false);

	const contractFormRef = useRef(null);

	const { token } = useSelector(s => s.login);

	function checkFormValidity() {
		if (contractFormRef.current) {
			const isValid = contractFormRef.current.isFormValid();
			setIsFormValid(isValid);
		}
	}

	function handleGenerateContract() {
		if (contractFormRef.current) {
			const formData = contractFormRef.current.collectFormData();

			if (formData) {
				setContractData(formData);
				setStep("confirmContractGeneration");
			}
		}
	}

	async function handleConfirmContractGeneration() {
		if (!contractData) {
			toast.error(
				"Houve um erro ao gerar o contrato. Tente novamente mais tarde."
			);
			return;
		}

		setIsGeneratingContract(true);

		try {
			const response = await api.sendPost(`/contracts/admin`, contractData, {
				Authorization: "Bearer " + token
			});

			const result = await response.json();

			if (result.isValid) {
				toast.success("Contrato gerado com sucesso.");
				setIsModalOpen(false);
				setStep("selectContractType");
				onContractGenerated && onContractGenerated();
			} else {
				const errorStr = result?.errors?.default?.includes("email")
					? result?.errors?.default
					: "Houve um erro ao gerar o contrato. Tente novamente mais tarde.";

				toast.error(errorStr);
			}
		} catch (error) {
			toast.error(
				"Houve um erro ao gerar o contrato. Tente novamente mais tarde."
			);
			console.log(error);
		} finally {
			setIsGeneratingContract(false);
		}
	}

	useEffect(() => {
		console.log("Dados do contrato:", contractData);
	}, [contractData]);

	return (
		<StyledModal
			open={isModalOpen}
			handleClose={() => {
				setStep("selectContractType");
				setIsModalOpen(false);
			}}
			scrollable={true}
		>
			<ModalHeader>
				<p>
					Geração de contrato da Solicitação #{solicitationId?.padStart(6, "0")}
				</p>
				<button
					onClick={() => {
						setStep("selectContractType");
						setIsModalOpen(false);
					}}
				>
					<X size={24} color="#8e8e93" />
				</button>
			</ModalHeader>

			<Separator />

			{step === "selectContractType" && (
				<SelectContractTypeStep setStep={setStep} />
			)}

			{(step === "mainContract" || step === "referenceContract") && (
				<ContractFormStep
					ref={contractFormRef}
					step={step}
					contractSolicitation={contractSolicitation}
					lastDocumentName={lastDocumentName}
					lastDocumentURL={lastDocumentURL}
					solicitationId={solicitationId}
					onValidationChange={checkFormValidity}
				/>
			)}

			{step === "confirmContractGeneration" && (
				<ConfirmContractGenerationStep solicitationId={solicitationId} />
			)}

			<ButtonsContainer>
				{step === "selectContractType" ? (
					<button onClick={() => setIsModalOpen(false)}>Fechar</button>
				) : step === "mainContract" || step === "referenceContract" ? (
					<>
						<button
							type="button"
							onClick={() => {
								setStep("selectContractType");
								setIsModalOpen(false);
							}}
						>
							Cancelar
						</button>
						<button onClick={handleGenerateContract} disabled={!isFormValid}>
							Gerar vínculo
						</button>
					</>
				) : (
					<>
						<button
							type="button"
							onClick={() => {
								setStep("selectContractType");
								setIsModalOpen(false);
							}}
						>
							Cancelar
						</button>
						<button
							onClick={handleConfirmContractGeneration}
							disabled={isGeneratingContract}
						>
							{isGeneratingContract ? <Spinner size="sm" /> : "Confirmar"}
						</button>
					</>
				)}
			</ButtonsContainer>
		</StyledModal>
	);
}
